import router from '../../../router'
import moment from 'moment'
import { COMPANY_STATUSES } from '@/utils/constants/drs/company-statuses'
import * as Sentry from '@sentry/vue'
import { TIMELINES } from '@/utils/constants/drs/tariffs'

export const TEST_SERVICE_CODE = 'drs'
export const SERVICE_CODE = 'drs2'
export const KPL_SERVICE_CODE = 'kpl'

const state = () => ({
    serviceId: null,
    isServiceActive: false,
    locations: [],
    isPermission: false,
    subscriptions: [],
    activeServiceEpLocations: [],
    userData: null,
    companiesData: [],
    allLocationsIntegrated: null,
    companyStatus: null,
    isKplServiceChecked: false
})

const mutations = {
    SET_SERVICE_ID(state, payload) {
        state.serviceId = payload
    },
    SET_SERVICE_ACTIVE(state, payload) {
        state.isServiceActive = payload
    },
    SET_LOCATIONS(state, payload) {
        state.locations = payload
    },
    SET_PERMISSION(state, payload) {
        state.isPermission = payload
    },
    SET_SUBSCRIPTIONS(state, payload) {
        state.subscriptions = payload
    },
    SET_ACTIVE_SERVICE_EP_LOCATIONS(state, payload) {
        state.activeServiceEpLocations = payload
    },
    SET_USER_DATA(state, payload) {
        state.userData = payload
    },
    SET_COMPANIES_DATA(state, payload) {
        state.companiesData = payload
    },
    SET_ALL_LOCATIONS_INTEGRATED(state, payload) {
        state.allLocationsIntegrated = payload
    },
    SET_COMPANY_STATUS(state, payload) {
        state.companyStatus = payload
    },
    SET_IS_KPL_SERVICE_CHECKED(state, status) {
        state.isKplServiceChecked = status
    }
}

const actions = {
    async checkServiceActivationStatus({ dispatch, commit, rootGetters }) {
        await dispatch('servicesFetch', null, { root: true })

        const drsService = rootGetters.getServiceByCode(SERVICE_CODE)

        if (!drsService) return

        const isActive = ((drsService || {}).number_of_entities_with_subscription || 0) > 0

        commit('SET_SERVICE_ID', drsService.id)
        commit('SET_SERVICE_ACTIVE', isActive)
    },
    async fetchCompaniesData({ commit }) {
        try {
            const { data, status } =
                await this.$api.get('drs/api/proxy/users/[drs_user_id]/basicData')

            if (status !== 200 || !data?.companiesData) {
                throw new Error('Error fetchDrsBasicData')
            }

            commit('SET_USER_DATA', data.userData)
            commit('SET_COMPANIES_DATA', data.companiesData)
        } catch (error) {
            console.error(error)
        }
    },
    async fetchLocationsWithCompanyId({ commit, dispatch, state }) {
        try {
            if (!state.companiesData.length) {
                // Fetch drs/basicData
                await dispatch('fetchCompaniesData')
            }

            // Fetch drs/extendedData
            const { data: extendedData, status: extendedDataStatus } = await this.$api.get('drs/api/proxy/users/[drs_user_id]/extendedData?includes=locations,companies')

            if (extendedDataStatus !== 200 || !extendedData) {
                throw new Error('Error fetchDrsExtendedData')
            }

            // Map locationId to companyId
            const companyMap = Object.fromEntries(
                state.companiesData.flatMap(company =>
                    company.locationsData.map(loc => [loc.id, company.id])
                )
            )

            // Filter locations by source, add companyId andfullAddress
            const locations = extendedData.locations
                .filter(loc => loc.source === 'EuroPlatform')
                .map(({ address, ep_location_id, ...rest }) => {
                    const { street, building, local, postal_code, city } = address || {}

                    return {
                        ...rest,
                        ep_location_id,
                        address,
                        companyId: companyMap[rest.id] || null,
                        fullAddress: `${street} ${building}${local ? '/' + local : ''}, ${postal_code} ${city}`
                    }
                })

            commit('SET_LOCATIONS', locations)

            return locations
        } catch (error) {
            console.error(error)
        }
    },
    async loadDataAndCheckPermission({ dispatch, state, commit, getters, rootGetters }) {
        if (rootGetters.isMultiNip || rootGetters.isShopUser) {
            commit('SET_PERMISSION', false)
            router.push({ name: 'permission-denied' })
            return
        }

        // check if drs service is active
        if (!state.isServiceActive) {
            await dispatch('checkServiceActivationStatus')
        } else {
            /**
             * Change value, so activation button reflects current state
             * (Activate Service button should change to Configure Tariff)
             */
            commit('SET_SERVICE_ACTIVE', true)
        }

        await dispatch('fetchCompaniesData')

        const promises = [
            dispatch('getCurrentTransaction'),
            dispatch('loadSubscriptions')
        ]

        // load locations if service is active
        if (state.isServiceActive && !state.locations.length) {
            promises.push(dispatch('fetchLocationsWithCompanyId'))
        }

        // load active subscriptions for drs service
        if (!state.activeServiceEpLocations.length) {
            promises.push(dispatch('loadActiveServiceEpLocations'))
        }

        await Promise.all(promises)

        // set drs permission
        const { locations, isServiceActive } = state

        const subscriptionsActive =
            getters.shopsWithActiveSubscription.length > 0 ||
            getters.shopsWithExpiringSubscription.length > 0

        commit('SET_PERMISSION',
            isServiceActive &&
            locations.length &&
            subscriptionsActive &&
            getters.isCompanyVerified)
    },

    async loadSubscriptions({ commit, rootGetters, dispatch, getters }) {
        try {
            const { data, status } = await this.$api.get(
                'drs/api/proxy/zor/subscriptions',
                {
                    params: {
                        nip: getters.nip,
                        service_code: 'KEC'
                    }
                }
            )

            if (status !== 200 || !data?.length) {
                throw new Error('Failed to load DRS shops')
            }

            let tariffs = rootGetters['drsTariffs/tariffs']

            if (!tariffs.length) {
                await dispatch('drsTariffs/loadDrsTariffs', null, { root: true })
                tariffs = rootGetters['drsTariffs/tariffs']
            }

            const subscriptions = data.map(sub => {
                if (Array.isArray(sub.current_subscription)) {
                    return sub
                }
                return {
                    ...sub,
                    current_subscription: {
                        ...sub.current_subscription,
                        isCurrentTariff: tariffs.some(
                            tariff => tariff.id === sub.current_subscription.tariff_id
                        )
                    }
                }
            })

            commit('SET_SUBSCRIPTIONS', subscriptions)
        } catch (error) {
            // console.error('Error loading DRS shops:', error)
        }
    },

    async loadActiveServiceEpLocations({ commit, state, rootGetters, dispatch }) {
        try {
            // fetch DRS test service details
            const { status, data } = await this.$api.get(
                `subscriptions_services/${rootGetters.userId}/services/${state.serviceId}`,
                { params: { subscriptionsWithEntityData: true } }
            )

            if (status !== 200 || !data?.data) {
                throw new Error(data.message || 'Error loadActiveServiceEpLocations')
            }

            const activeLocationIds = data.data.subscriptions
                .filter(({ status }) => status === 'active')
                .map(({ entityData }) => entityData.id)

            commit('SET_ACTIVE_SERVICE_EP_LOCATIONS', activeLocationIds)
        } catch (error) {
            console.error(error)
        }
    },

    async getCurrentTransaction({ commit, getters, rootGetters }) {
        try {
            const { data } = await this.$api.get('/drs/api/proxy/zor/payments/current_transaction', {
                params: {
                    nip: getters.nip,
                    service_code: 'KEC'
                }
            })

            commit('SET_COMPANY_STATUS', data?.state)
        } catch (error) {
            Sentry.withScope((scope) => {
                scope.setLevel('error')
                scope.setTag('module', 'drs')
                scope.setTag('endpoint', 'drs/api/proxy/zor/payments/current_transaction')
                scope.setTag('file', 'drs.module.js')
                scope.setContext('details', {
                    dateTime: new Date().toISOString(),
                    userId: rootGetters.userId,
                    nip: getters.nip
                })

                Sentry.captureException(error)
            })

            console.error('Error fetching current transaction:', error)
        }
    },

    async loadAndCheckExternalData({ commit, getters, dispatch, rootGetters }) {
        const locations = getters.shopsWithActiveService

        if (getters.allLocationsIntegrated || !locations.length) return

        await dispatch('drsOperators/fetchOperators', null, { root: true })

        const operatorServiceIds = rootGetters['drsOperators/getOperators'].map(op => op.serviceId)

        const requests = locations.map(shop =>
            dispatch(
                'drsOperators/fetchExternalData',
                { locationId: shop.id, operatorServiceIds },
                { root: true }
            )
        )

        const results = await Promise.all(requests)

        const allLocationsIntegrated = results.every(externalData => {
            if (!externalData || !externalData.length) return false

            return externalData.some(op => op.collection_point_id)
        })

        commit('SET_ALL_LOCATIONS_INTEGRATED', allLocationsIntegrated)
    },
    async activateMissingKplService({ commit, getters, state }) {
        // skip if already checked or no locations with active service
        if (state.isKplServiceChecked || !getters.shopsWithActiveService.length) return

        try {
            // load drs locations with active services
            const { data: locationsData, status: locationsStatus } = await this.$api.get(
                'drs/api/aggregate/v3/shop_list'
            )

            if (locationsStatus !== 200 || !locationsData?.[0]?.locations) {
                throw new Error('Error fetchShopList')
            }

            // set locations to activate
            const activeLocIds = getters.shopsWithActiveService.map(el => el.id)
            const locationsToActivate = activeLocIds.filter(locId => {
                const locActiveServices = locationsData[0].locations.find(el => el.id === locId)?.active_services || []
                return locActiveServices.findIndex(service => service.code === KPL_SERVICE_CODE) < 0
            })

            // skip if no locations to activate
            if (!locationsToActivate.length) {
                commit('SET_IS_KPL_SERVICE_CHECKED', true)
                return
            }

            // load DRS services
            const { data: servicesData, status: servicesStatus } = await this.$api.get(
                'drs/api/proxy/subscriptions/[drs_user_id]/services'
            )
            if (servicesStatus !== 200 || !servicesData?.services) {
                throw new Error('Error fetchDrsServices')
            }

            // find KPL service
            const kplService = servicesData.services.find(
                el => el.code === KPL_SERVICE_CODE
            )
            if (!kplService) {
                throw new Error('KPL service not found')
            }

            // activate KPL service
            const { status: activateServiceStatus } = await this.$api.patch(
                `drs/api/proxy/subscriptions/users/[drs_user_id]/services/${kplService.id}/activate`,
                {
                    activation_level: kplService.level.toUpperCase(),
                    objectIds: locationsToActivate
                },
                {
                    headers: { source: 'PD' }
                }
            )

            if (activateServiceStatus !== 200) {
                throw new Error('Error activateKplService')
            }

            commit('SET_IS_KPL_SERVICE_CHECKED', true)
        } catch (error) {
            console.error('KPL activation failed:', error)
        }
    }
}

const getters = {
    getServiceId: state => state.serviceId,
    isServiceActive: state => state.isServiceActive,
    getLocations: state => state.locations,
    isPermission: state => state.isPermission,
    getSubscriptions: state => state.subscriptions,
    shopsWithActiveService: state => {
        return state.locations.filter(({ ep_location_id }) =>
            state.activeServiceEpLocations.includes(ep_location_id))
    },
    shopsWithoutActiveSubscription: (state, getters) => {
        const subscriptions = state.subscriptions

        if (!getters.isCompanyVerified) {
            return getters.shopsWithActiveService
        }

        return getters.shopsWithActiveService.filter(location => {
            const sub = subscriptions.find(sub => sub.bo_location_id === location.id)
            if (!sub) {
                return true
            }

            const currentSubscription = sub?.current_subscription
            let isSubscriptionActive = false

            /**
             * API is weird - when currentSubscription is not empty, it becomes object
             * When it's empty, it stays as an array...
             */
            if (Array.isArray(currentSubscription)) {
                if (currentSubscription.length > 0) {
                    isSubscriptionActive = true
                }
            } else {
                // filter only subscriptions with isCurrentTariff: false
                isSubscriptionActive = !!currentSubscription.isCurrentTariff
            }

            return !isSubscriptionActive
        })
    },
    shopsWithActiveSubscription: (state, getters) => {
        const subscriptions = state.subscriptions

        return getters.shopsWithActiveService.filter(location => {
            const sub = subscriptions.find(sub => sub.bo_location_id === location.id)
            if (!sub) {
                return false
            }

            return !!sub.current_subscription.isCurrentTariff
        })
    },
    shopsWithExpiringSubscription: (state, getters, _, rootGetters) => {
        const subscriptions = state.subscriptions

        return getters.shopsWithActiveService.filter(location => {
            const sub = subscriptions.find(sub => sub.bo_location_id === location.id)
            if (!sub) {
                return false
            }

            const isCurrentTariff = rootGetters['drsTariffs/tariffs'].some(
                tariff => tariff.id === sub.current_subscription.tariff_id
            )

            if (isCurrentTariff) {
                return false
            }

            if (Array.isArray(sub.expiring_subscription) || !sub.expiring_subscription) {
                return false
            }

            const currentDate = moment()
            const subscriptionEndDate = moment(sub.expiring_subscription.date_to)

            // If subscription end date is before the end of the current date, it means it's expiring
            return currentDate.isBefore(subscriptionEndDate)
        })
    },
    getLocationsForFilters: (_, getters) => {
        return getters.shopsWithActiveService.map(m =>
            ({ value: m.id, label: `${m.full_name}, ${m.fullAddress}` }))
    },
    getSubscriptionByLocationId: (state) => (locationId) => {
        const subscription = state.subscriptions.find(sub => sub.bo_location_id === locationId)

        if (!subscription) {
            return null
        }

        const currentSubscription = subscription.current_subscription

        return Array.isArray(currentSubscription) ? null : currentSubscription
    },
    isCompanyVerified: state => state.companyStatus === COMPANY_STATUSES.VERIFIED,
    userData: state => state.userData,
    companiesData: state => state.companiesData,
    nip: state => state.companiesData[0]?.nip,
    getAllSubscriptionsByLocationId: (state) => (locationId) => {
        const subscription = state.subscriptions.find(sub => sub.bo_location_id === locationId)

        if (!subscription) {
            return null
        }

        const currentSubscription = subscription.current_subscription
        const expiringSubscription = subscription.expiring_subscription

        return {
            current: Array.isArray(currentSubscription) ? null : currentSubscription,
            expiring: Array.isArray(expiringSubscription) ? null : expiringSubscription
        }
    },
    companyStatus: state => state.companyStatus,
    allLocationsIntegrated: state => state.allLocationsIntegrated,
    cancelableShops: (_, getters) => {
        // Yearly subscriptions cannot be canceled
        return getters.shopsWithActiveService.filter(location => {
            const subscription = getters.getSubscriptionByLocationId(location.id)

            return subscription && subscription.tariff_timeline !== TIMELINES.YEARLY
        })
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
